import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { authTokenInterceptor } from './core/api/interceptors/auth-token.interceptor';
import { errorInterceptor } from './core/api/interceptors/error.interceptor';
import { requestIdInterceptor } from './core/api/interceptors/request-id.interceptor';
import { APP_CONFIG, ENGINE_API_BASE_URL } from './core/config/app-config';

/** `true` para las URL del motor (`${environment.APIEndPoint}/agents-engine`, `/…` o `?…`), nunca para el resto de Sirius. */
export function isAgentsEngineRequest(url: string, base = ENGINE_API_BASE_URL): boolean {
  const root = base.replace(/\/+$/, '');
  return url === root || url.startsWith(`${root}/`) || url.startsWith(`${root}?`);
}

/**
 * Interceptor funcional compuesto de la feature «Agentes IA» (§4.2 del inventario), el único archivo de la feature
 * que `AppModule` importa de forma eager: `provideHttpClient(withInterceptors([agentsEngineInterceptor]),
 * withInterceptorsFromDi())`. Solo actúa sobre las URL del motor; para cualquier otra petición de Sirius es un
 * `next(req)` y los interceptores de clase (`AuthInterceptor`, `HttpInterceptService`) siguen corriendo igual.
 *
 * La base del motor es `APP_CONFIG.apiBaseUrl`, la misma con la que `ApiClient` construye sus URL (una sola
 * fuente; por defecto `ENGINE_API_BASE_URL`, derivada de `environment.APIEndPoint`).
 *
 * Sobre el motor encadena los tres interceptores de la consola: `Authorization: Bearer` (independiente del
 * `byPassUrls` de `HttpInterceptService`, que salta las URL con `.json`), `x-request-id` y la política de errores
 * (401 → cierre de sesión, 403 → aviso de access, resto → notificación; `TimeoutError` tolerado). Los servicios de
 * `core/api` son `providedIn: 'root'` e inyectan el `HttpClient` raíz, por eso el interceptor vive en `AppModule`.
 */
export const agentsEngineInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isAgentsEngineRequest(req.url, inject(APP_CONFIG).apiBaseUrl)) return next(req);
  return authTokenInterceptor(req, withToken =>
    requestIdInterceptor(withToken, withId => errorInterceptor(withId, next)),
  );
};
